import { useEffect, useMemo } from "react";
import { SkipNextOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { MuiMarkdown } from 'mui-markdown';

export default function JoyrideSoothTooltip({
  step,
  index,
  continuous,
  backProps,
  primaryProps,
  closeProps,
  tooltipProps,
  isLastStep,
  skipProps,
  size,
}) {
  const { hideBackButton, title, content } = step;
  const progress = useMemo(() => {
    return ((index + 1) / size) * 100;
  }, [index, size]);

  function handleKeyDown(event) {
    event.preventDefault();
    switch (event.code) {
      case "Space":
        if (continuous) {
          primaryProps.onClick(event);
        } else {
          closeProps.onClick(event);
        }
        break;
      case "Escape":
        if (continuous && skipProps != null) {
          skipProps.onClick(event);
        } else if (!continuous) {
          closeProps.onClick(event);
        }
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <Card
      elevation={10}
      {...tooltipProps}
      sx={{
        minWidth: "15vw",
        maxWidth: "40vw",
        border: "4px solid #1b99e8",
      }}
    >
      <CardHeader
        disableTypography
        title={
          <Stack direction="row" alignItems="baseline" justifyContent="end">
            {title != null && (
              <Typography
                width="auto"
                component="div"
                variant="h5"
                noWrap
                sx={{ flex: 1 }}
              >
                {title}
              </Typography>
            )}
          </Stack>
        }
      />
      <CardContent
        sx={{
          pt: 0,
          "&>*:first-child": {
            mt: 0,
          },
          "&>*:last-child": {
            mb: 0,
          },
        }}
      >
        <MuiMarkdown>{content}</MuiMarkdown>
      </CardContent>
      <LinearProgress variant="determinate" color="primary" value={progress} />
      <CardActions
        sx={({ palette }) => ({
          backgroundColor: palette.secondary.main,
          justifyContent: "end",
        })}
      >
        {skipProps && (
          <Box sx={{ flex: 1 }}>
            <Tooltip title="Press 'Esc' to skip">
              <Button
                size="small"
                color="primary"
                onClick={skipProps.onClick}
                variant="text"
                endIcon={<SkipNextOutlined fontSize="small" />}
                sx={({ typography }) => ({
                  ...typography.caption,
                })}
              >
                Skip Tour
              </Button>
            </Tooltip>
          </Box>
        )}
        <Box>
          {!hideBackButton && index > 0 && (
            <Button variant="outlined" onClick={backProps.onClick}>
              {backProps.title}
            </Button>
          )}
          <Tooltip
            enterDelay={1000}
            title={`Press 'Space' to ${isLastStep ? "end" : "continue"}`}
          >
            {continuous ? (
              <Button onClick={primaryProps.onClick}>
                {isLastStep
                  ? "End"
                  : `${primaryProps.title} (${index + 1}/${size})`}
              </Button>
            ) : (
              <Button onClick={closeProps.onClick}>{closeProps.title}</Button>
            )}
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
}
